import { apEventSpec } from './apAnalytics'
import {
  billsAnalytics,
  billsEventFields,
  isBillsId,
  safeBillsProperties,
  type BillsAttempt,
  type BillsEvent
} from './apBills'

const legacyFields = [
  'billId',
  'billID',
  'billIDs',
  'count',
  'successCount',
  'failedCount',
  'selectedCount',
  'requestId',
  'operationId',
  'actionAttemptId',
  'errorId',
  'errorCode',
  'hasReason'
]
export const billsLegacyEvents = new Set([
  'submitBillForApprovalSuccess',
  'submitMultipleBillsForApprovalSuccess',
  'markBillPaidExternallySuccess',
  'deleteBillSuccess',
  'deleteMultipleBillsSuccess',
  'cancelPaymentSuccess',
  'bulkCancelPaymentsSuccess',
  'updateBillPayDateSuccess',
  'payBillSuccess',
  'downloadBillSuccess'
])
export const billsSharedProperties = (
  properties: Record<string, unknown> = {}
) => {
  const safe: Record<string, unknown> = {}
  const safeName = (value: unknown) =>
    typeof value === 'string' && /^[a-zA-Z][a-zA-Z0-9_-]{0,79}$/.test(value)
  for (const key of [
    'table_id',
    'column_id',
    'moved_over_column_id',
    'parentId',
    'itemId'
  ])
    if (safeName(properties[key])) safe[key] = properties[key]
  for (const key of ['is_customized', 'is_visible', 'willOpen', 'isCollapsed'])
    if (typeof properties[key] === 'boolean') safe[key] = properties[key]
  if (['sidebar', 'flyout'].includes(String(properties.source)))
    safe.source = properties.source
  if (typeof properties.destination === 'string')
    safe.destination = sanitizeBillsUrl(properties.destination)
  for (const key of ['columns_order', 'previous_order'])
    if (Array.isArray(properties[key]))
      safe[key] = properties[key].filter(safeName)
  for (const key of ['final_columns', 'columns_before_reset'])
    if (Array.isArray(properties[key]))
      safe[key] = properties[key]
        .filter(column => safeName(column?.id))
        .map(column => ({ id: column.id, visible: column.visible === true }))
  return safe
}
const routePattern = /^\/[A-Za-z0-9/_[\]-]{0,119}$/
// router.pathname only. shortcut: no dots, so catch-all [...slug] is dropped; upgrade if a catch-all page mounts.
export const instrumentationProperties = (
  properties: Record<string, unknown>
) => ({
  ...(isBillsId(properties.request_id)
    ? { request_id: properties.request_id }
    : {}),
  ...(typeof properties.route === 'string' &&
  routePattern.test(properties.route)
    ? { route: properties.route }
    : {})
})
export const billsLegacyProperties = (
  properties: Record<string, unknown> = {},
  attempt?: BillsAttempt
): Record<string, unknown> => ({
  ...safeBillsProperties(
    [
      'screenVisitId',
      'originScreenVisitId',
      'userId',
      'accountId',
      'partnerId',
      'tab'
    ],
    { ...(attempt ?? billsAnalytics.context() ?? {}) }
  ),
  ...(attempt ?? billsAnalytics.context()
    ? {
        screenId: 'bills_list',
        environment: (attempt ?? billsAnalytics.context())?.environment,
        trafficClass: (attempt ?? billsAnalytics.context())?.trafficClass
      }
    : {}),
  ...safeBillsProperties(legacyFields, properties),
  ...billsSharedProperties(properties),
  ...safeBillsProperties(
    properties.actionAttemptId == null && properties.operationId == null
      ? ['actionAttemptId', 'operationId', 'requestId', 'inputMethod']
      : ['requestId', 'inputMethod'],
    { ...(attempt ?? billsAnalytics.activeAttempt() ?? {}) }
  ),
  ...instrumentationProperties(properties)
})

const automaticFields = new Set([
  '$raw_user_agent',
  '$user_agent',
  '$current_url',
  '$referrer',
  '$pathname',
  '$host',
  '$session_id',
  '$window_id',
  '$insert_id',
  '$lib',
  '$lib_version',
  '$browser',
  '$browser_version',
  '$os',
  '$os_version',
  '$device_type',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
  '$timezone',
  '$time',
  '$is_identified',
  '$user_id',
  'distinct_id',
  // $identify carries this; dropping it splits the anonymous visitor from the person.
  '$anon_distinct_id',
  // posthog-js sends the project key only here; without it ingestion
  // refuses the event with a 401.
  'token'
])
// PostHog's own metadata, set by app code rather than typed on AP screens: flag
// variants, the release, and the person properties a `$set` event carries.
export const posthogMetadata = (event: {
  event: string
  properties: Record<string, unknown>
}) => {
  const safe: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(event.properties)) {
    if (
      (key === '$feature_flag' ||
        key === '$feature_flag_response' ||
        key.startsWith('$feature/')) &&
      ['string', 'boolean'].includes(typeof value)
    )
      safe[key] = value
    else if (key === '$release' && typeof value === 'string') safe[key] = value
    else if (key === '$active_feature_flags' && Array.isArray(value))
      safe[key] = value.filter(flag => typeof flag === 'string')
    else if (
      (key === '$set' || key === '$set_once') &&
      event.event === '$set' &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    )
      safe[key] = value
  }
  return safe
}
const commonFields = [
  'originSessionId',
  'eventId',
  'schemaVersion',
  'screenId',
  'screenVisitId',
  'originScreenVisitId',
  'userId',
  'accountId',
  'partnerId',
  'tab',
  'environment',
  'trafficClass',
  'timestamp',
  'actionAttemptId',
  'requestId',
  'operationId',
  'inputMethod'
]
export const sanitizeBillsUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  try {
    const url = new URL(value, 'https://app.alternativepayments.io')
    url.search = ''
    url.hash = ''
    url.username = ''
    url.password = ''
    const path = url.pathname.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ':id')
    const knownRoute =
      /^\/dashboard\/accounts-payable(?:\/(?:bills|vendors)(?:\/(?:create|:id)(?:\/(?:details|edit|receiving-method))?)?)?\/?$/.test(
        path
      )
    url.pathname = knownRoute ? path : '/'
    return url.toString()
  } catch {
    return undefined
  }
}

// Bills list events, and anything else captured on the list pathname
// (/dashboard/accounts-payable/bills, query string allowed). Create, edit and
// details stay with apAnalyticsPrivacy. Dictionary events do too.
export const isBillsScopedEvent = (event: {
  event: string
  properties: Record<string, unknown>
}) => {
  const properties = event.properties
  let onList = false
  if (typeof properties.$current_url === 'string')
    try {
      onList =
        new URL(properties.$current_url, 'https://app.alternativepayments.io')
          .pathname === '/dashboard/accounts-payable/bills'
    } catch {
      // A malformed URL is not the list.
    }
  return (
    apEventSpec(event.event) === undefined &&
    (properties.screenId === 'bills_list' ||
      properties.originScreenId === 'bills_list' ||
      onList)
  )
}

export const sanitizeBillsEvent = <
  T extends { event: string; properties: Record<string, unknown> }
>(
  event: T
): T | null => {
  const properties = event.properties
  if (!isBillsScopedEvent(event)) return event
  // Explicit, safe events cover the listing. Automatic DOM/exception payloads
  // can contain bill numbers, vendor names, filenames or provider messages.
  if (
    [
      '$autocapture',
      '$exception',
      '$snapshot',
      '$heatmaps',
      '$rageclick',
      '$dead_click'
    ].includes(event.event)
  )
    return null
  const safe: Record<string, unknown> = {}
  for (const key of automaticFields) {
    if (
      key !== '$current_url' &&
      key !== '$referrer' &&
      properties[key] !== undefined
    )
      safe[key] = properties[key]
  }
  Object.assign(safe, posthogMetadata(event))
  for (const key of ['$current_url', '$referrer']) {
    const url = sanitizeBillsUrl(properties[key])
    if (url) safe[key] = url
  }
  if (typeof safe.$pathname === 'string') {
    const pathname = sanitizeBillsUrl(safe.$pathname)
    safe.$pathname = pathname ? new URL(pathname).pathname : undefined
  }
  const name = event.event.replace(/^apBills\./, '') as BillsEvent
  if (event.event.startsWith('apBills.') && name in billsEventFields) {
    Object.assign(
      safe,
      safeBillsProperties(
        [...billsEventFields[name].split(' '), ...commonFields],
        properties
      )
    )
  } else
    Object.assign(
      safe,
      safeBillsProperties([...legacyFields, ...commonFields], properties),
      billsSharedProperties(properties)
    )
  for (const key of ['screenId', 'environment', 'trafficClass', 'timestamp']) {
    const value = properties[key]
    if (
      (key === 'screenId' && value === 'bills_list') ||
      (key === 'environment' &&
        typeof value === 'string' &&
        ['production', 'staging', 'development'].includes(value)) ||
      (key === 'trafficClass' &&
        typeof value === 'string' &&
        [
          'partner_self_service',
          'internal',
          'test',
          'impersonated',
          'assisted',
          'unknown'
        ].includes(value)) ||
      (key === 'timestamp' &&
        typeof value === 'string' &&
        /^\d{4}-\d\d-\d\dT[\d:.]+Z$/.test(value))
    )
      safe[key] = value
  }
  Object.assign(safe, instrumentationProperties(properties))
  event.properties = safe
  return event
}
